import first from "lodash/first";
import { Fragment, type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import Placeholder from "scmp-app/components/newsletter/newsletter-category-item-slideshow/placeholder.svg";
import type { bundledNewsletterItemNewsletter$key } from "scmp-app/queries/__generated__/bundledNewsletterItemNewsletter.graphql";

import {
  Container,
  Content,
  Footer,
  ImageContainer,
  Including,
  Name,
  NewsletterNames,
  PlaceholderContainer,
  StyledBaseImage,
  StyledSignUpButton,
} from "./styles";

type Props = {
  className?: string;
  displayName: string;
  reference: bundledNewsletterItemNewsletter$key;
};

export const BundledNewsletterItem: FunctionComponent<Props> = ({
  className,
  displayName,
  reference,
}) => {
  const newsletters = useFragment(
    graphql`
      fragment bundledNewsletterItemNewsletter on Newsletter @relay(plural: true) {
        name
        images {
          title
          url
        }
        ...signUpButtonNewsletter
      }
    `,
    reference,
  );

  const renderImage = useCallback(() => {
    const newsletterImages = newsletters
      .map(item => first(item.images ?? []))
      .filter(notEmpty)
      .slice(0, 4);

    if (notEmpty(newsletterImages)) {
      return (
        <ImageContainer>
          {newsletterImages.map((newsletterImage, index) => (
            <Fragment key={index}>
              <StyledBaseImage
                $aspectRatio="3/2"
                $objectFit="cover"
                alt={newsletterImage?.title ?? ""}
                src={newsletterImage.url}
              />
              {newsletterImages.length === 2 && index === 0 && (
                <>
                  <div />
                  <div />
                </>
              )}
            </Fragment>
          ))}
        </ImageContainer>
      );
    }
    return (
      <PlaceholderContainer>
        <Placeholder />
      </PlaceholderContainer>
    );
  }, [newsletters]);

  return (
    <Container className={className}>
      {renderImage()}

      <Content>
        {displayName && <Name>{displayName}</Name>}
        <Including>including:</Including>
        <NewsletterNames>
          {newsletters.map(({ name }, index) => (
            <Fragment key={index}>
              <span>{name}</span>
              {index < newsletters.length - 1 && <span> / </span>}
            </Fragment>
          ))}
        </NewsletterNames>
      </Content>
      <Footer>
        <StyledSignUpButton packageName={displayName} reference={newsletters} />
      </Footer>
    </Container>
  );
};

BundledNewsletterItem.displayName = "BundledNewsletterItem";
